import { Carousel, Headline, Layout } from '@oneaudi/unified-web-components';
import { useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { VATConfig } from '~/app/market-config';
import {
	StyledDialogActionButtonWrapper,
	StyledSingleImage,
} from '~/app/pages/configurator/components/more-information-dialog/components/more-information-dialog-header/styles/more-information-dialog-header-style';
import { Disclaimer } from '~/components/disclaimer/disclaimer';
import { ProductInformationText } from '~/components/product-information';
import { RenderHtml } from '~/components/text';
import { PriceFragment, StatusFragment } from '~/graphql/__generated__/graphql';
import { isDisclaimerAvailable } from '~/utils/date';
import { DialogItemActionButton } from '../dialog-item-action-button';

export function MoreInformationDialogHeader({
	headline,
	imageUrl,
	imageUrls,
	status,
	price,
	monthlyRateOverride,
	pr7,
	vatConfig,
	onAddToConfiguration,
	imageFit = 'object-cover',
	hidePriceAndAvailability,
	disclaimerHeadline,
	disclaimerContent,
	disclaimerStartDate,
	disclaimerEndDate,
}: {
	headline: string;
	imageUrl: string | null | undefined;
	imageUrls: string[];
	status: StatusFragment;
	price: PriceFragment | undefined | null;
	monthlyRateOverride?: number | undefined | null;
	pr7?: string | undefined;
	vatConfig: VATConfig;
	onAddToConfiguration: (() => void) | undefined;
	/** Workaround to adapt object-fit for rims */
	imageFit?: 'object-contain' | 'object-cover';
	hidePriceAndAvailability?: boolean;
	disclaimerHeadline?: string;
	disclaimerContent?: string;
	disclaimerStartDate?: string;
	disclaimerEndDate?: string;
}) {
	return (
		<Layout direction={'column'} rowGap="24px">
			{imageUrl && imageUrls?.length <= 1 && (
				<StyledSingleImage objectFit={imageFit} src={imageUrl} alt={headline} loading="lazy" />
			)}
			{imageUrls?.length > 1 && <MoreInformationDialogSlider imageUrls={imageUrls} altText={headline} />}

			<Layout direction={'column'} rowGap="24px">
				{/* eslint-disable-next-line */}
				<div tabIndex={0}>
					<Layout direction={'column'} rowGap="16px">
						<Headline tag={'h2'} variant={{ type: 'headline', order: '3', weight: 'bold' }}>
							<RenderHtml html={headline} />
						</Headline>

						{!hidePriceAndAvailability && (
							<ProductInformationText
								vatConfig={vatConfig}
								status={status}
								price={price}
								monthlyRateOverride={monthlyRateOverride}
								pr7={pr7}
							/>
						)}
					</Layout>
				</div>

				{typeof onAddToConfiguration === 'function' && (
					<StyledDialogActionButtonWrapper>
						<DialogItemActionButton status={status} onAddToConfiguration={onAddToConfiguration} />
					</StyledDialogActionButtonWrapper>
				)}
				{disclaimerContent && isDisclaimerAvailable(disclaimerStartDate, disclaimerEndDate) && (
					/* eslint-disable-next-line */
					<div tabIndex={0}>
						<Disclaimer type="item" headline={disclaimerHeadline} content={disclaimerContent} />
					</div>
				)}
			</Layout>
		</Layout>
	);
}

export function MoreInformationDialogSlider({ imageUrls, altText }: { imageUrls: string[]; altText: string }) {
	const { i18n } = useI18n();
	const [active, setActive] = useState(1);
	return (
		<Carousel
			ariaSlideLabel={(index: number, total: number, alt: string) =>
				`${i18n('ngc.a11y.carousel.slide', { index: index + 1, total })} - ${alt}`
			}
			current={active}
			items={{
				decrementElement: {
					'aria-label': i18n('ngc.a11y.carousel.previousImage'),
					onClick: () => setActive(active - 1),
				},
				incrementElement: {
					'aria-label': i18n('ngc.a11y.carousel.nextImage'),
					onClick: () => setActive(active + 1),
				},
				items: imageUrls.map((imgUrl, index) => ({
					src: imgUrl,
					// Currently we don't have alt text for images in the dialog, so we leave it empty. Needs to be implemented by cola.
					// For now, we use the altText parameter passed to the component.
					alt: altText,
					onClick: () => setActive(index + 1),
				})),
			}}
			variant="vertical"
		/>
	);
}
